import { useQuery } from '@tanstack/react-query'

import { useAuth } from '@/auth/AuthProvider'
import { configApi } from '@/lib/api'
import type { ClientConfig } from '@/types/domain'

/**
 * The deployment's client-facing flags and limits — `GET /v1/config`.
 *
 * The shell reads them instead of inferring them: whether the verification
 * gate exists at all, when it pauses this account's uploads, and whether any
 * plan limit is enforced. Every one of those used to be guessed from
 * something else (a 403 that had already happened, `emailVerified` on the
 * profile, a usage response that reports limits either way).
 *
 * Only asked with a session, for the reason `useUsage` documents: an
 * authenticated request from a public route drives `apiClient`'s
 * refresh-and-retry and can race the bootstrap refresh, which the gateway
 * answers by revoking the whole session family.
 *
 * `retry: false` and a long `staleTime` because this is policy, not data: it
 * changes with a deployment, and a screen must never wait on it — every
 * caller has an honest reading for "not answered yet".
 */
export function useClientConfig() {
  const { signedIn } = useAuth()
  return useQuery<ClientConfig>({
    queryKey: ['config'],
    queryFn: () => configApi.get(),
    enabled: signedIn,
    staleTime: 5 * 60_000,
    retry: false,
  })
}
